import { Question } from "../types";

/** Take up to `roomLeft` questions without splitting an EMQ group across the cut. */
export function takeBatchPreservingEmqGroups(
  batch: Question[],
  roomLeft: number,
): Question[] {
  if (roomLeft <= 0 || batch.length === 0) {
    return [];
  }

  const result: Question[] = [];
  let i = 0;

  while (i < batch.length) {
    const question = batch[i];
    const groupingId = question.emqPart?.groupingId;

    if (groupingId == null) {
      if (result.length >= roomLeft) {
        break;
      }
      result.push(question);
      i += 1;
      continue;
    }

    let j = i + 1;
    while (j < batch.length && batch[j].emqPart?.groupingId === groupingId) {
      j += 1;
    }
    const group = batch.slice(i, j);

    if (result.length + group.length > roomLeft) {
      // Never split a group. If the batch would otherwise be empty, take the
      // whole group so pagination can still make progress.
      if (result.length === 0) {
        return group;
      }
      break;
    }

    result.push(...group);
    i = j;
  }

  return result;
}

/** Grouping ids that have fewer loaded parts than partsCount. */
export function getIncompleteEmqGroupingIds(questions: Question[]): number[] {
  const byGroup = new Map<number, { loaded: number; partsCount: number }>();

  for (const question of questions) {
    const part = question.emqPart;
    if (!part?.groupingId || part.partsCount == null) {
      continue;
    }
    const existing = byGroup.get(part.groupingId) ?? {
      loaded: 0,
      partsCount: part.partsCount,
    };
    existing.loaded += 1;
    existing.partsCount = part.partsCount;
    byGroup.set(part.groupingId, existing);
  }

  return Array.from(byGroup.entries())
    .filter(([, info]) => info.loaded < info.partsCount)
    .map(([groupingId]) => groupingId);
}

export function hasIncompleteEmqGroups(questions: Question[]): boolean {
  return getIncompleteEmqGroupingIds(questions).length > 0;
}
